import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Plus, Pencil, Trash, Trophy, CheckCircle } from "@phosphor-icons/react";

const CURRENT_YEAR = new Date().getFullYear();

const emptyForm = () => ({
  year: CURRENT_YEAR,
  title: "",
  description: "",
  target_type: "revenue",  // "revenue" (€) o "number" (numero)
  target_value: 0,
});

export default function ObjectivesAdmin() {
  const [objectives, setObjectives] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [year, setYear] = useState(CURRENT_YEAR);

  const load = async () => {
    const r = await api.get(`/annual-objectives?year=${year}`);
    setObjectives(r.data || []);
  };
  useEffect(() => { load(); }, [year]);

  const openNew = () => { setEditing(null); setForm({ ...emptyForm(), year }); setDialogOpen(true); };
  const openEdit = (o) => {
    setEditing(o);
    setForm({
      year: o.year,
      title: o.title,
      description: o.description || "",
      target_type: o.target_type === "revenue" ? "revenue" : "number",
      target_value: o.target_value,
    });
    setDialogOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error("Il titolo è obbligatorio"); return; }
    // Backend supporta revenue | practice_count | service_count. Mappo "number" a "practice_count".
    const target_type = form.target_type === "revenue" ? "revenue" : "practice_count";
    const payload = {
      year: parseInt(form.year) || CURRENT_YEAR,
      title: form.title.trim(),
      description: form.description || "",
      target_type,
      target_value: parseFloat(form.target_value) || 0,
    };
    try {
      if (editing) await api.patch(`/annual-objectives/${editing._id}`, payload);
      else await api.post("/annual-objectives", payload);
      toast.success(editing ? "Obiettivo aggiornato" : "Obiettivo creato");
      setDialogOpen(false);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };

  const remove = async (o) => {
    if (!window.confirm(`Eliminare l'obiettivo "${o.title}"?`)) return;
    try {
      await api.delete(`/annual-objectives/${o._id}`);
      toast.success("Obiettivo eliminato");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };

  const fmt = (v, isRevenue) => isRevenue ? `€${Number(v).toLocaleString("it-IT")}` : `${Number(v).toLocaleString("it-IT")}`;

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-baseline gap-3">
          <h3 className="font-display text-xl font-semibold">Obiettivi Annuali</h3>
          <select value={year} onChange={e => setYear(parseInt(e.target.value))}
            className="h-9 border rounded px-2 text-sm bg-background" data-testid="year-selector">
            {[CURRENT_YEAR - 1, CURRENT_YEAR, CURRENT_YEAR + 1].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <Button onClick={openNew} className="ht-gradient border-0" data-testid="new-objective-btn">
          <Plus size={16} className="mr-2" weight="bold" /> Nuovo Obiettivo
        </Button>
      </div>

      {objectives.length === 0 ? (
        <div className="kpi-card text-center py-16 text-muted-foreground">
          <Trophy size={40} weight="duotone" className="mx-auto mb-3 text-purple-400" />
          <p>Nessun obiettivo definito per il {year}.</p>
          <p className="text-xs mt-2">Crea il primo obiettivo per iniziare a tracciare il progresso.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {objectives.map(o => {
            const isRevenue = o.target_type === "revenue";
            const currentValue = o.current_value || 0;
            const pct = Math.min(100, Math.round(o.progress_pct || 0));
            const reached = currentValue >= o.target_value;
            return (
              <div key={o._id} className="kpi-card" data-testid={`objective-${o._id}`}>
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <div className="label-eyebrow">{isRevenue ? "Fatturato €" : "Numero pratiche"} · {o.year}</div>
                    <h4 className="font-display text-lg font-bold mt-1">{o.title}</h4>
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(o)} data-testid={`edit-obj-${o._id}`}><Pencil size={14} /></Button>
                    <Button size="sm" variant="ghost" onClick={() => remove(o)} className="text-red-600" data-testid={`delete-obj-${o._id}`}><Trash size={14} /></Button>
                  </div>
                </div>
                {o.description && <p className="text-xs text-muted-foreground mb-3">{o.description}</p>}
                <div className="flex items-baseline justify-between text-sm mb-2">
                  <span className="font-mono font-semibold">{fmt(currentValue, isRevenue)}</span>
                  <span className="text-muted-foreground">/ {fmt(o.target_value, isRevenue)}</span>
                </div>
                <div className="h-2 bg-[color:hsl(var(--surface-alt))] rounded-full overflow-hidden">
                  <div className={`h-full transition-all ${reached ? "bg-green-500" : "ht-gradient"}`} style={{ width: `${pct}%` }} />
                </div>
                <div className="text-xs mt-2 flex items-center gap-1">
                  {reached ? (
                    <span className="text-green-700 font-semibold flex items-center gap-1">
                      <CheckCircle size={14} weight="fill" /> Raggiunto ({pct}%)
                    </span>
                  ) : (
                    <span className="text-muted-foreground">{pct}% completato</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? "Modifica obiettivo" : "Nuovo obiettivo annuale"}</DialogTitle>
            <DialogDescription>Definisci un traguardo annuo (fatturato o numero di pratiche).</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <div>
              <Label>Titolo *</Label>
              <Input required value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
                placeholder="es. APE 2000" data-testid="obj-title" />
            </div>
            <div>
              <Label>Descrizione (opzionale)</Label>
              <Textarea rows={2} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                placeholder="Note interne sull'obiettivo" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Anno</Label>
                <Input type="number" value={form.year} onChange={e => setForm({ ...form, year: e.target.value })} />
              </div>
              <div>
                <Label>Tipo</Label>
                <select value={form.target_type} onChange={e => setForm({ ...form, target_type: e.target.value })}
                  className="w-full h-10 border rounded px-3 text-sm bg-background" data-testid="obj-type">
                  <option value="revenue">€ Fatturato</option>
                  <option value="number">Numero pratiche</option>
                </select>
              </div>
            </div>
            <div>
              <Label>{form.target_type === "revenue" ? "Obiettivo €" : "Numero pratiche target"} *</Label>
              <Input type="number" min="0" step={form.target_type === "revenue" ? "0.01" : "1"}
                required value={form.target_value}
                onChange={e => setForm({ ...form, target_value: e.target.value })}
                className="font-mono" data-testid="obj-value" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)}>Annulla</Button>
              <Button type="submit" className="ht-gradient border-0" data-testid="save-obj">
                {editing ? "Salva modifiche" : "Crea obiettivo"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
